import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ScanCartSection,
  OrderCard,
  ProductList,
  ProcessedProductsSection,
  FinishedBanner,
  OrdersHeader,
} from '../display/receive';
import { Breadcrumb } from '../display';
import { FinishProcessedButton } from '../interaction/FinishProcessedButton';
import { useReceiveReturned, useReceiveLogic, useRegisteredData } from '../../hooks';

interface ReceiveContainerProps {
  returnedId: number;
}

export const ReceiveContainer = ({ returnedId }: ReceiveContainerProps) => {
  const { t } = useTranslation('receiveModule');
  const navigate = useNavigate();

  // 🎯 Búsqueda de pedidos (local)
  const [ordersSearch, setOrdersSearch] = useState('');
  // 🎯 Búsqueda de productos procesados (local)
  const [productsSearch, setProductsSearch] = useState('');

  // 🎯 Obtener datos del rehusado
  const { returnedData, orders, isLoading, ordersLoading, receiveLines, finalizeReturned, isReceiving } =
    useReceiveReturned(returnedId);

  // 🎯 Hook principal con toda la lógica
  const {
    selectedOrder,
    processedOrders,
    processedProductsQuantities,
    processedProductsTotals,
    scannedQuantities,
    flashingProducts,
    ordersSortBy,
    productsSortBy,
    confirmModalRef,
    flattenedProducts,
    productsCount,
    sortedProcessedOrders,
    sortedProcessedProducts,
    handleOrderFound,
    handleOrdersSort,
    handleProductsSort,
    handleLocateProcessed,
    handleProductScan,
    handleBatchChange,
    validateQuantities,
    selectedBatchIds,
  } = useReceiveLogic(returnedId, () => navigate(`/returned/${returnedId}/locate`));

  // Enviar pedido a la API para recepcionar (con validación previa)
  const handleProcessOrderApi = async () => {
    if (!selectedOrder) return;

    // Validar cantidades antes de enviar
    const validation = validateQuantities();

    if (validation.type === 'none') {
      // Sin problemas, enviar directamente
      await sendToApi();
    } else {
      // Mostrar modal de confirmación según el tipo de validación
      confirmModalRef.current?.open(
        async () => {
          await sendToApi();
        },
        undefined,
        validation.title,
        validation.message,
      );
    }
  };

  // Función auxiliar para enviar a la API
  const sendToApi = async () => {
    if (!selectedOrder) return;

    // Construir líneas a partir de productos escaneados
    const lines: { product_id: number; quantity: number; batch_id?: number }[] = [];
    flattenedProducts.forEach((p: any) => {
      const qty = (scannedQuantities as any)?.[p.code] || 0;
      if (qty > 0) {
        const batchId = selectedBatchIds[p.id] ?? null;
        lines.push({
          product_id: Number(p.id),
          quantity: Number(qty),
          ...(batchId && { batch_id: batchId }),
        });
      }
    });

    // Enviar con la estructura que espera la API: { order_id, lines }
    receiveLines(
      { order_id: Number(selectedOrder.id), lines },
      {
        onSuccess: () => {
          // Resetear selección para volver al paso 1 cuando la API responde OK
          handleOrderFound(null as any);
          // Forzar refetch del listado principal
          // (la invalidación primaria se hace en el hook)
        },
      },
    );
  };

  // Función para finalizar el rehusado
  const handleFinalizeReturned = () => {
    finalizeReturned();
  };

  // 🎯 Determinar si mostrar datos registrados (estado "por ubicar" o "cerrado" O si hay pedidos asociados)
  const shouldShowRegisteredData = Boolean(
    returnedData &&
    (returnedData.status === 2 ||
      returnedData.status === 0 ||
      (returnedData.orders_count && returnedData.orders_count > 0)),
  );

  // 🎯 Hook para datos registrados
  const { registeredOrders, sortedRegisteredProducts, sortedRegisteredOrders } = useRegisteredData({
    shouldShowRegisteredData,
    returnedData,
    orders,
    sortProducts: (products: any[], sortBy: string) => {
      // Función de sort simplificada para datos registrados
      return [...products].sort((a, b) => {
        switch (sortBy) {
          case 'more_qty': {
            const aQty = (a as any).processedQuantity || 0;
            const bQty = (b as any).processedQuantity || 0;
            return bQty - aQty;
          }
          case 'less_qty': {
            const aQty = (a as any).processedQuantity || 0;
            const bQty = (b as any).processedQuantity || 0;
            return aQty - bQty;
          }
          case 'name_az':
            return a.name.localeCompare(b.name);
          case 'name_za':
            return b.name.localeCompare(a.name);
          default:
            return 0;
        }
      });
    },
    sortOrders: (orders: any[], sortBy: string) => {
      // Función de sort simplificada para órdenes registradas
      return [...orders].sort((a, b) => {
        switch (sortBy) {
          case 'more_qty': {
            const aCount = a.lines.reduce((acc: any, line: any) => acc + (line.pack?.lines.length || 1), 0);
            const bCount = b.lines.reduce((acc: any, line: any) => acc + (line.pack?.lines.length || 1), 0);
            return bCount - aCount;
          }
          case 'less_qty': {
            const aCount = a.lines.reduce((acc: any, line: any) => acc + (line.pack?.lines.length || 1), 0);
            const bCount = b.lines.reduce((acc: any, line: any) => acc + (line.pack?.lines.length || 1), 0);
            return aCount - bCount;
          }
          case 'name_az':
            return a.ref.localeCompare(b.ref);
          case 'name_za':
            return b.ref.localeCompare(a.ref);
          default:
            return 0;
        }
      });
    },
    productsSortBy,
    ordersSortBy,
  });

  // 🎯 Estado de carga unificado - Aplicar opacidad en lugar de spinner
  // Esperar también a que lleguen todas las órdenes esperadas si hay líneas con order_id
  const expectedOrdersCount = Array.from(
    new Set((returnedData?.lines || []).map((l: any) => l?.order_id).filter(Boolean)),
  ).length;
  const shouldWaitOrders = expectedOrdersCount > 0 && (orders?.length || 0) < expectedOrdersCount;

  // 🎯 Estado de carga completo
  const isFullyLoading = isLoading || ordersLoading || shouldWaitOrders || !returnedData;

  // 🎯 Renderizado principal del componente
  return (
    <>
      <div className="flex justify-between items-center w-full px-2 sm:px-0">
        <Breadcrumb returnedId={returnedId} code={returnedData?.code} status={returnedData?.status} />
      </div>

      <div
        className={`flex flex-col sm:flex-row gap-2 h-full w-full sm:border sm:border-card-border sm:rounded-lg transition-opacity duration-300 ${isFullyLoading ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}
      >
        {/* Columna Izquierda */}
        <div className="w-full md:w-[50%] flex flex-col gap-5 p-4 sm:h-full sm:border-r sm:border-card-border sm:max-h-[calc(100vh-55px)] overflow-y-auto">
          {/* Scan header - Solo mostrar si no está cerrado ni por ubicar */}
          {returnedData?.status !== 0 && returnedData?.status !== 2 && (
            <div className="w-full mb-2">
              <ScanCartSection
                returnedId={returnedId}
                returnedData={returnedData}
                onOrderFound={handleOrderFound}
                hasOrder={!!selectedOrder}
                onProductScan={handleProductScan}
              />
            </div>
          )}

          {/* Cartel para estado "Por ubicar" */}
          {returnedData?.status === 2 && <FinishedBanner />}

          {/* Pedidos */}
          <div className="flex flex-col gap-4">
            {/* Header - Pedidos rehusados (solo cuando no hay pedido seleccionado) */}
            {!selectedOrder ? (
              <>
                <OrdersHeader
                  shouldShowRegisteredData={shouldShowRegisteredData}
                  registeredCount={registeredOrders.length}
                  processedCount={processedOrders.length}
                  ordersSearch={ordersSearch}
                  setOrdersSearch={setOrdersSearch}
                  ordersSortBy={ordersSortBy}
                  handleOrdersSort={handleOrdersSort}
                />

                {/* Lista de pedidos procesados o registrados */}
                <div className="space-y-4 mt-4">
                  {(() => {
                    const filterOrders = (ordersArr: any[]) => {
                      const q = ordersSearch.toLowerCase().trim();
                      if (!q) return ordersArr;
                      return ordersArr.filter(
                        (order) => (order.ref?.toLowerCase() || '').includes(q) || (String(order.id) || '').includes(q),
                      );
                    };

                    if (shouldShowRegisteredData) {
                      const filtered = filterOrders(sortedRegisteredOrders);
                      const content = filtered.map((order) => (
                        <OrderCard
                          key={order.id}
                          order={order}
                          productsCount={order.lines.reduce(
                            (acc: number, line: any) => acc + (line.pack?.lines.length || 1),
                            0,
                          )}
                          isProcessed={true}
                        />
                      ));
                      return (
                        <>
                          {content}
                          {ordersSearch.trim() && filtered.length === 0 && (
                            <div className="sm:mt-16 text-center text-complementary-text text-sm">
                              {t('items.search.no_results.title')}
                            </div>
                          )}
                        </>
                      );
                    } else {
                      const filtered = filterOrders(sortedProcessedOrders);
                      const content = filtered.map((order) => (
                        <OrderCard
                          key={order.id}
                          order={order}
                          productsCount={order.lines.reduce(
                            (acc: number, line: any) => acc + (line.pack?.lines.length || 1),
                            0,
                          )}
                          isProcessed={true}
                        />
                      ));
                      return (
                        <>
                          {content}
                          {ordersSearch.trim() && filtered.length === 0 && (
                            <div className="sm:mt-16 text-center text-complementary-text text-sm">
                              {t('items.search.no_results.title')}
                            </div>
                          )}
                        </>
                      );
                    }
                  })()}
                </div>

                {/* Estado vacío */}
                {((shouldShowRegisteredData && registeredOrders.length === 0) ||
                  (!shouldShowRegisteredData && processedOrders.length === 0)) && (
                  <div className="sm:mt-16 text-center text-complementary-text text-sm mb-6 sm:mb-0">
                    {t('orders.empty.description')}
                  </div>
                )}
              </>
            ) : (
              <OrderCard
                order={selectedOrder}
                productsCount={productsCount}
                onProcessOrder={handleProcessOrderApi}
                isReceiving={isReceiving}
              >
                <ProductList
                  products={flattenedProducts}
                  scannedQuantities={scannedQuantities}
                  flashingProducts={flashingProducts}
                  selectedBatchIds={selectedBatchIds}
                  onBatchChange={handleBatchChange}
                />
              </OrderCard>
            )}
          </div>
        </div>
        {/* Columna Derecha */}
        <div className="w-full md:w-[50%] sm:max-h-[calc(100vh-55px)] overflow-y-auto sm:h-full">
          <ProcessedProductsSection
            processedProducts={shouldShowRegisteredData ? sortedRegisteredProducts : sortedProcessedProducts}
            processedProductsQuantities={shouldShowRegisteredData ? {} : processedProductsQuantities}
            processedProductsTotals={shouldShowRegisteredData ? {} : processedProductsTotals}
            onSort={handleProductsSort}
            currentSort={productsSortBy}
            status={returnedData?.status}
            searchQuery={productsSearch}
            onSearchChange={setProductsSearch}
          />
        </div>
      </div>

      {/* Botón de finalizar - Mostrar siempre excepto cuando esté cerrado */}
      {returnedData && returnedData.status !== 0 && (
        <div className="absolute right-4 top-2">
          <FinishProcessedButton
            onFinish={handleFinalizeReturned}
            onLocate={handleLocateProcessed}
            status={returnedData?.status || 1}
            disabled={
              returnedData.status === 1 &&
              (shouldShowRegisteredData ? registeredOrders.length === 0 : processedOrders.length === 0)
            }
          />
        </div>
      )}

      {/* Modal de confirmación para pedidos sin productos */}
      <ConfirmModal ref={confirmModalRef} />
    </>
  );
};
